import { useEffect, useId, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { defaultCountryCode } from '@components/country-code/constants';
import {
  GROUP_MEMBER_MAX_AMOUNT,
  GROUP_MEMBER_MIN_AMOUNT,
  NAME_MAX_LENGTH,
  NAME_MIN_LENGTH
} from '@constants/generic';
import { PaymentCollectionType } from '@features/group-management/constants';
import {
  CUSTOM_TERM_DUE_DATE_ORDER_ERROR,
  isDateBefore
} from '@features/group-management/helpers/customTerm';
import DuplicateAdmissionModal from '@features/member-management/components/DuplicateAdmissionModal';
import useNotify from '@hooks/notify';
import {
  AddMemberFormDataType,
  AddMemberModalType,
  AddMemberProps,
  MemberListType,
  SectionHeaders,
  SectionHeadersType,
  TermsList,
  ValidationError
} from '@types';
import { roundTo2 } from '@utils/generic';

import GroupMemberModalSidebar, {
  GroupMemberSidebarItem
} from './GroupMemberModalSidebar';
import {
  addMemberFormDataInitialState,
  getFormattedAddMemberData,
  hasChangesInMemberDetails,
  isMemberSaveDisabled
} from './helper';
import MemberAddress from './MemberAddress';
import MemberBasicInfo from './MemberBasicInfo';
import MemberCustomFields from './MemberCustomFields';
import MemberMoreInfo from './MemberMoreInfo';
import MemberPersonalInfo from './MemberPersonalInfo';

import ModalV2 from '../../layouts/modal/Modal.v2';

const AddMemberModal = ({
  type = AddMemberModalType.ADD,
  isOpen,
  handleCancel,
  addMember,
  paymentCollectionType,
  selectedmember,
  groupAmount,
  groupDueDate,
  groupFrequency,
  groupStartDate,
  groupEndDate,
  isFeeCategoryEnabled = false,
  feeCategoryData,
  feeCategoriesValues,
  memberList,
  isImmediatePayment = false,
  isCustomTerm = false,
  termsList = [],
  validateMember,
  isValidationLoading = false,
  isAttendanceBasedFee = false,
  isSessionBased = false,
  groupSessionPerCycle,
  defaultTermLabel
}: AddMemberProps) => {
  const id = useId();

  const { orgConfig, appConfig } = useSelector((state: any) => state.user);
  const { memberCustomFields } = useSelector(
    (state: any) => state.branchConfig
  );

  const groupDetailsRef = useRef<HTMLElement>(null);
  const personalInfoRef = useRef<HTMLElement>(null);
  const addressRef = useRef<HTMLElement>(null);
  const moreInfoRef = useRef<HTMLElement>(null);
  const customFieldsRef = useRef<HTMLElement>(null);

  const hasCustomFields = memberCustomFields?.length > 0;

  const sidebarItems: GroupMemberSidebarItem[] = [
    {
      id: SectionHeaders.GROUP_DETAILS,
      label: 'Group details',
      icon: 'group-details-icon',
      ref: groupDetailsRef
    },
    {
      id: SectionHeaders.PERSONAL_INFO,
      label: 'Personal information',
      icon: 'members-icon',
      ref: personalInfoRef
    },
    {
      id: SectionHeaders.ADDRESS,
      label: 'Address details',
      icon: 'marker-pin',
      ref: addressRef
    },
    {
      id: SectionHeaders.MORE_INFO,
      label: 'More info',
      icon: 'menu',
      ref: moreInfoRef
    },
    ...(hasCustomFields
      ? [
          {
            id: SectionHeaders.CUSTOM_FIELDS,
            label: 'Custom Fields',
            icon: 'edit-file-transparent',
            ref: customFieldsRef
          }
        ]
      : [])
  ];

  const [formData, setFormData] = useState<AddMemberFormDataType>({
    ...addMemberFormDataInitialState,
    amount: groupAmount || '',
    dueDate: {
      day: groupDueDate || '',
      paymentCollectionType,
      interval: String(groupFrequency) || '1'
    },
    startDate: groupStartDate || new Date().toString(),
    endDate: '',
    termsList,
    totalFee: selectedmember?.totalFee || '',
    sessionsPerCycle: groupSessionPerCycle ?? 0
  });
  const [isCalendarVisible, setIsCalendarVisible] = useState<boolean>(false);
  const [memberId, setMemberId] = useState<string>('');
  const [error, setError] = useState<ValidationError>({});
  const [focusedSidebarItem, setFocusedSidebarItem] =
    useState<SectionHeadersType>(SectionHeaders.GROUP_DETAILS);

  const [isDuplicateModalOpen, setIsDuplicateModalOpen] =
    useState<boolean>(false);
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [pendingAction, setPendingAction] = useState<
    'save' | 'saveAndAddAnother' | null
  >(null);
  const [pendingMemberData, setPendingMemberData] =
    useState<MemberListType | null>(null);
  const [warningAdmissionNumber, setWarningAdmissionNumber] =
    useState<string>('');

  const { createErrorAlert } = useNotify();

  const groupMemberMaxAmount =
    orgConfig?.group_member_amount_max || GROUP_MEMBER_MAX_AMOUNT;
  const groupMemberMinAmount =
    orgConfig?.group_member_amount_min || GROUP_MEMBER_MIN_AMOUNT;
  const memberNameMaxLength =
    orgConfig?.member_name_length_max || NAME_MAX_LENGTH;
  const memberNameMinLength =
    orgConfig?.member_name_length_min || NAME_MIN_LENGTH;

  const handleFormDataChange = (
    field: keyof typeof formData,
    value: string
  ) => {
    setFormData((prevFormData: AddMemberFormDataType) => ({
      ...prevFormData,
      [field]: value
    }));
  };

  const onLine1Change = (value: string) => {
    handleFormDataChange('addressLine1', value);
  };

  const onLine2Change = (value: string) => {
    handleFormDataChange('addressLine2', value);
  };

  const onCountryChange = (value: string) => {
    if (value === 'Others') {
      handleFormDataChange('state', '');
      handleFormDataChange('city', '');
      handleFormDataChange('pincode', '');
    }
    handleFormDataChange('country', value);
  };

  const onStateChange = (value: string) => {
    handleFormDataChange('state', value);
  };

  const onCityChange = (value: string) => {
    handleFormDataChange('city', value);
  };

  const onPincodeChange = (value: string) => {
    setError({ ...error, pincode: '' });
    handleFormDataChange('pincode', value);
  };

  const onBlurPincode = (value: string) => {
    if (value && value.length !== 6)
      setError({ ...error, pincode: 'Invalid pincode' });
  };

  const getIsDueDateInPast = (row: TermsList) => {
    const isDueDatePast =
      new Date(new Date(row.dueDate).setHours(0, 0, 0, 0)) <
      new Date(new Date().setHours(0, 0, 0, 0));
    return isDueDatePast;
  };

  const validateForm = () => {
    const hasDuplicate = memberList.some(
      (member) =>
        `${member.name}_${member.primaryNumberCountryCode}${member.primaryNumber}` ===
          `${formData.name}_${formData.primaryCountryCode}${formData.primaryNumber}` &&
        member.id !== memberId
    );

    if (hasDuplicate) {
      createErrorAlert('Member already exists');
      return false;
    }

    if (isCustomTerm && formData.termsList) {
      const pastDueTerm = formData.termsList.find(
        (term) => getIsDueDateInPast(term) && Number(term.dueAmount) !== 0
      );
      if (pastDueTerm) {
        setError({
          ...error,
          dueDate: "Due date can't be in the past",
          dueDateRowId: pastDueTerm.id
        });
        return false;
      }

      const violatingTerm = formData.termsList.find(
        (term, index) =>
          index > 0 &&
          isDateBefore(
            term.dueDate,
            formData.termsList[index - 1].dueDate,
            true
          )
      );
      if (violatingTerm) {
        setError({
          ...error,
          dueDate: CUSTOM_TERM_DUE_DATE_ORDER_ERROR,
          dueDateRowId: violatingTerm.id
        });
        return false;
      }
    }
    return true;
  };

  const getFeeConfigValue = (categoryItem: any, index: number) => {
    const category = selectedmember?.feeCategoryData?.find(
      (item) =>
        item.id === (categoryItem.branch_fee_category_id ?? categoryItem.id)
    );
    if (category?.isManuallyUpdated) return category.value;
    return (
      (feeCategoriesValues && feeCategoriesValues[index]?.value) ||
      categoryItem.amount ||
      categoryItem.value ||
      0
    );
  };

  const isFeeConfigUpdated = (categoryItem: any) => {
    const category = selectedmember?.feeCategoryData?.find(
      (item) =>
        item.id === (categoryItem.branch_fee_category_id ?? categoryItem.id)
    );
    return !!category?.isManuallyUpdated;
  };

  const resetFeeCategory = () => {
    const formattedData = feeCategoryData?.map((item: any, index: number) => ({
      id: item.branch_fee_category_id ?? item.id,
      label: item.category || item.label,
      value: getFeeConfigValue(item, index),
      isManuallyUpdated: isFeeConfigUpdated(item)
    }));

    setFormData((prev) => ({
      ...prev,
      feeCategories: formattedData || []
    }));
  };

  const getFeeConfigTotalAmount = () =>
    roundTo2(
      feeCategoryData?.reduce(
        (sum, item, index) => sum + Number(getFeeConfigValue(item, index)),
        0
      ) || 0
    ).toString() || '0';

  const handleReset = () => {
    setFormData({
      ...addMemberFormDataInitialState,
      amount: groupAmount || '',
      dueDate: {
        day: groupDueDate || '',
        paymentCollectionType,
        interval: String(groupFrequency) || '1'
      },
      startDate: groupStartDate || new Date().toString(),
      endDate: '',
      termsList,
      totalFee: '',
      sessionsPerCycle: groupSessionPerCycle ?? 0
    });
    setIsCalendarVisible(false);
    setFocusedSidebarItem(SectionHeaders.GROUP_DETAILS);
    resetFeeCategory();
    setIsDuplicateModalOpen(false);
    setPendingAction(null);
    setPendingMemberData(null);
    setWarningAdmissionNumber('');
  };

  const executeSaveAction = (
    memberData: MemberListType,
    action: 'save' | 'saveAndAddAnother'
  ) => {
    addMember(memberData);
    if (action === 'save') {
      handleCancel();
    } else {
      handleReset();
    }
  };

  const handleSaveWithValidation = async (
    action: 'save' | 'saveAndAddAnother'
  ) => {
    const isValid = validateForm();
    if (!isValid) return;

    const sessionsPerCycle = Number(
      formData.sessionsPerCycle ?? groupSessionPerCycle
    );

    const customFields = memberCustomFields || [];
    const formattedData = {
      ...getFormattedAddMemberData(
        formData,
        type,
        memberId,
        id,
        groupAmount,
        groupDueDate,
        customFields
      ),
      ...(isSessionBased && { sessionsPerCycle })
    };

    if (validateMember) {
      setIsValidating(true);
      try {
        const validationResult = await validateMember(formattedData);

        if (validationResult.hasError) {
          createErrorAlert(
            validationResult.errorMessage || 'Validation failed'
          );
          setIsValidating(false);
          return;
        }

        if (validationResult.hasWarning) {
          setPendingAction(action);
          setPendingMemberData(formattedData);
          setWarningAdmissionNumber(validationResult.admissionNumber || '');
          setIsDuplicateModalOpen(true);
          setIsValidating(false);
          return;
        }

        setIsValidating(false);
        executeSaveAction(formattedData, action);
      } catch {
        createErrorAlert('Validation failed. Please try again.');
        setIsValidating(false);
      }
    } else {
      addMember(formattedData);
      if (action === 'save') {
        handleCancel();
      } else {
        handleReset();
      }
    }
  };

  const onSave = () => {
    handleSaveWithValidation('save');
  };

  const onSaveAndAddAnother = () => {
    handleSaveWithValidation('saveAndAddAnother');
  };

  const handleDuplicateModalCancel = () => {
    setIsDuplicateModalOpen(false);
    setPendingAction(null);
    setPendingMemberData(null);
    setWarningAdmissionNumber('');
  };

  const handleDuplicateModalProceed = () => {
    if (pendingMemberData && pendingAction) {
      executeSaveAction(pendingMemberData, pendingAction);
    }
    setIsDuplicateModalOpen(false);
    setPendingAction(null);
    setPendingMemberData(null);
    setWarningAdmissionNumber('');
  };

  const hasError = Object.values(error).some((value) => value !== '');
  const hasChanges = hasChangesInMemberDetails(
    formData,
    selectedmember,
    memberCustomFields || []
  );

  const isMemberDataInvalid = isMemberSaveDisabled(
    formData,
    groupMemberMaxAmount,
    groupMemberMinAmount,
    memberNameMaxLength,
    memberNameMinLength,
    appConfig.supported_phone_number_countries,
    isFeeCategoryEnabled
  );
  const isSaveDisabled =
    isMemberDataInvalid ||
    !hasChanges ||
    hasError ||
    isValidating ||
    isValidationLoading;

  const onSidebarItemClick = (containerRef?: React.RefObject<HTMLElement>) => {
    if (!containerRef?.current) return;
    const inputs = containerRef.current.getElementsByTagName('input');
    Array.from(inputs)
      .find((el) => !el.disabled)
      ?.focus();
  };

  useEffect(() => {
    if (type === AddMemberModalType.EDIT) {
      setFormData((prev) => ({
        ...prev,
        name: selectedmember?.name || '',
        primaryNumber: selectedmember?.primaryNumber || '',
        primaryCountryCode:
          selectedmember?.primaryNumberCountryCode || defaultCountryCode,
        altNumber: selectedmember?.altNumber || '',
        altCountryCode:
          selectedmember?.altNumberCountryCode || defaultCountryCode,
        amount:
          isFeeCategoryEnabled && feeCategoryData && !isCustomTerm
            ? getFeeConfigTotalAmount()
            : selectedmember?.amount || groupAmount || '',
        dueDate: {
          day: selectedmember?.dueDate || groupDueDate || '',
          interval: selectedmember?.interval || String(groupFrequency) || '1',
          paymentCollectionType:
            selectedmember?.paymentCollectionType ||
            paymentCollectionType ||
            PaymentCollectionType.MONTHLY
        },
        email: selectedmember?.email || '',
        addressLine1: selectedmember?.addressLine1 || '',
        addressLine2: selectedmember?.addressLine2 || '',
        country: selectedmember?.country || '',
        state: selectedmember?.state || '',
        city: selectedmember?.city || '',
        pincode: selectedmember?.pincode || '',
        startDate: selectedmember?.startDate || '',
        endDate: selectedmember?.endDate || '',
        totalFee: selectedmember?.totalFee || '',
        dateOfBirth: selectedmember?.dateOfBirth || '',
        bloodGroup: selectedmember?.bloodGroup || '',
        guardianName: selectedmember?.guardianName || '',
        admissionNumber: selectedmember?.admissionNumber || '',
        admissionDate: selectedmember?.admissionDate || '',
        counsellorName: selectedmember?.counsellorName || '',
        isInsured: selectedmember?.isInsured || '',
        discount: selectedmember?.discount?.length
          ? {
              isEnabled: true,
              label: selectedmember.discount[0].label || '',
              value: String(selectedmember.discount[0].value || '')
            }
          : { isEnabled: false, label: '', value: '' }
      }));
      setMemberId(selectedmember?.id || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  useEffect(() => {
    if (isCalendarVisible) {
      const element = document.getElementById('due-date-calendar');
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest'
        });
      }
    }
  }, [isCalendarVisible]);

  useEffect(() => {
    if (feeCategoryData) resetFeeCategory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(feeCategoryData)]);

  if (!isOpen) return null;

  return (
    <>
      <ModalV2
        title={type === AddMemberModalType.EDIT ? 'Edit Member' : 'Add Member'}
        onClose={handleCancel}
        className="w-4/6 min-w-225 !bg-cofeeGreyBlue50"
        bodyClassName="!p-0 !overflow-hidden"
        primaryButtonLabel="Save"
        primaryButtonClick={onSave}
        primaryButtonDisabled={isSaveDisabled}
        primaryButtonLoading={isValidating || isValidationLoading}
        secondaryButtonLabel={
          type === AddMemberModalType.ADD ? 'Save & Add Another' : undefined
        }
        secondaryButtonClick={
          type === AddMemberModalType.ADD ? onSaveAndAddAnother : undefined
        }
        secondaryButtonDisabled={isSaveDisabled}
        isClickOutsideRequired={!isDuplicateModalOpen}
      >
        <div className="grid grid-cols-3 gap-7 overflow-hidden pl-6 lg:grid-cols-5">
          <GroupMemberModalSidebar
            items={sidebarItems}
            focusedSidebarItem={focusedSidebarItem}
            onSidebarItemClick={onSidebarItemClick}
          />
          <div
            className="modal-scroll col-span-2 overflow-y-auto pb-10 pr-6 lg:col-span-4"
            style={{ maxHeight: '70vh' }}
          >
            <h2 className="my-4 text-lg font-semibold">Group details</h2>

            <MemberBasicInfo
              formData={formData}
              setError={setError}
              error={error}
              memberNameMaxLength={memberNameMaxLength}
              memberNameMinLength={memberNameMinLength}
              isFeeCategoryEnabled={isFeeCategoryEnabled}
              isImmediatePayment={isImmediatePayment}
              isCustomTerm={isCustomTerm}
              groupMemberMaxAmount={groupMemberMaxAmount}
              groupMemberMinAmount={groupMemberMinAmount}
              selectedmember={selectedmember}
              paymentCollectionType={paymentCollectionType}
              groupFrequency={groupFrequency}
              groupStartDate={groupStartDate}
              groupEndDate={groupEndDate}
              handleFormDataChange={handleFormDataChange}
              supported_phone_number_countries={
                appConfig.supported_phone_number_countries
              }
              setFormData={setFormData}
              setIsCalendarVisible={setIsCalendarVisible}
              isAttendanceBasedFee={isAttendanceBasedFee}
              isSessionBased={isSessionBased}
              sessionsPerCycle={
                formData.sessionsPerCycle ?? groupSessionPerCycle
              }
              containerRef={groupDetailsRef}
              onFocused={() =>
                setFocusedSidebarItem(SectionHeaders.GROUP_DETAILS)
              }
              defaultTermLabel={defaultTermLabel}
            />

            <h2 className="my-4 text-lg font-semibold">Personal information</h2>
            <MemberPersonalInfo
              formData={formData}
              handleFormDataChange={handleFormDataChange}
              setError={setError}
              error={error}
              containerRef={personalInfoRef}
              onFocused={() =>
                setFocusedSidebarItem(SectionHeaders.PERSONAL_INFO)
              }
            />

            <h2 className="my-4 text-lg font-semibold">Address details</h2>
            <MemberAddress
              formData={formData}
              onLine1Change={onLine1Change}
              onLine2Change={onLine2Change}
              onCountryChange={onCountryChange}
              onStateChange={onStateChange}
              onCityChange={onCityChange}
              onPincodeChange={onPincodeChange}
              pincodeError={error.pincode}
              onBlurPincode={() => onBlurPincode(formData.pincode)}
              containerRef={addressRef}
              onFocused={() => setFocusedSidebarItem(SectionHeaders.ADDRESS)}
            />

            <h2 className="my-4 text-lg font-semibold">More info</h2>
            <MemberMoreInfo
              formData={formData}
              handleFormDataChange={handleFormDataChange}
              setError={setError}
              error={error}
              containerRef={moreInfoRef}
              onFocused={() => setFocusedSidebarItem(SectionHeaders.MORE_INFO)}
            />

            {hasCustomFields && (
              <h2 className="my-4 text-lg font-semibold">Custom Fields</h2>
            )}
            <MemberCustomFields
              formData={formData}
              handleFormDataChange={handleFormDataChange}
              error={error}
              containerRef={customFieldsRef}
              onFocused={() =>
                setFocusedSidebarItem(SectionHeaders.CUSTOM_FIELDS)
              }
            />
          </div>
        </div>
      </ModalV2>
      <DuplicateAdmissionModal
        isOpen={isDuplicateModalOpen}
        admissionNumber={warningAdmissionNumber}
        isAdmissionNumberError={!!warningAdmissionNumber}
        isLoading={false}
        onCancel={handleDuplicateModalCancel}
        onProceed={handleDuplicateModalProceed}
      />
    </>
  );
};

export default AddMemberModal;
